import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { createTheme, ThemeProvider } from '@mui/material'
import { buildTheme } from 'theme'

const ThemeModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
})

/**
 * Detect system preference without using MUI's useMediaQuery
 * (which requires an existing ThemeProvider above it).
 */
const getSystemMode = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const ThemeModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const stored = localStorage.getItem('themeMode')
    if (stored === 'light' || stored === 'dark') return stored
    return getSystemMode()
  })

  // Listen for system preference changes (only when user has no manual preference)
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e) => {
      const stored = localStorage.getItem('themeMode')
      if (!stored) {
        setModeState(e.matches ? 'dark' : 'light')
      }
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const toggleMode = () => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', next)
      return next
    })
  }

  const setMode = (newMode) => {
    localStorage.setItem('themeMode', newMode)
    setModeState(newMode)
  }

  const muiTheme = useMemo(() => createTheme(buildTheme(mode)), [mode])

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode, setMode }}>
      <ThemeProvider theme={muiTheme}>
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext)
  if (!ctx) throw new Error('useThemeMode must be used inside ThemeModeProvider')
  return ctx
}

export { ThemeModeProvider, useThemeMode }
